import { useState } from 'react'
import { PRACTICE_STATUSES, type Practice, type PracticePatch } from '../../domain'
import common from '../common.module.css'
import { updatePreferences, usePreferences } from '../prefs'
import { t } from '../strings'
import styles from './Play.module.css'

interface PracticePanelProps {
  practice: Practice | undefined
  onChange: (patch: PracticePatch) => void
}

const STEPS = [2, 4, 5, 10]

/**
 * Memoria de práctica en los ajustes de la hoja: estado, notas y el último tempo cómodo, más los
 * ajustes de la guía que valen para todas las canciones (cuenta de entrada y paso de «Me sale»).
 */
export function PracticePanel({ practice, onChange }: PracticePanelProps) {
  const preferences = usePreferences()
  const p = t.practice
  const [notes, setNotes] = useState(practice?.notes ?? '')

  const saveNotes = () => {
    if (notes.trim() !== (practice?.notes ?? '')) onChange({ notes: notes.trim() || undefined })
  }

  return (
    <div>
      <h2>{p.title}</h2>
      <div className={styles.group} role="group" aria-label={p.status}>
        {PRACTICE_STATUSES.map((status) => (
          <button
            key={status}
            aria-pressed={practice?.status === status}
            className={practice?.status === status ? common.toggleOn : undefined}
            onClick={() => onChange({ status: practice?.status === status ? undefined : status })}
          >
            {p.statuses[status]}
          </button>
        ))}
      </div>
      <p className={styles.hint}>
        {practice?.comfortableBpm !== undefined
          ? p.comfortable(practice.comfortableBpm)
          : p.noComfortable}
      </p>
      <label className={common.field}>
        <span>{p.notes}</span>
        <textarea
          rows={3}
          value={notes}
          placeholder={p.notesPlaceholder}
          onChange={(event) => setNotes(event.target.value)}
          onBlur={saveNotes}
        />
      </label>
      <h2 style={{ marginTop: 14 }}>{p.countInTitle}</h2>
      <div className={styles.group}>
        {[0, 1, 2].map((bars) => (
          <button
            key={bars}
            aria-pressed={preferences.countInBars === bars}
            className={preferences.countInBars === bars ? common.toggleOn : undefined}
            onClick={() => updatePreferences((current) => ({ ...current, countInBars: bars }))}
          >
            {p.countInBars(bars)}
          </button>
        ))}
      </div>
      <h2 style={{ marginTop: 14 }}>{p.stepTitle}</h2>
      <div className={styles.group}>
        {STEPS.map((step) => (
          <button
            key={step}
            aria-pressed={preferences.speedStep === step}
            className={preferences.speedStep === step ? common.toggleOn : undefined}
            onClick={() => updatePreferences((current) => ({ ...current, speedStep: step }))}
          >
            {p.step(step)}
          </button>
        ))}
      </div>
    </div>
  )
}
